import { getAuth } from 'firebase/auth';

const SMARTTHINGS_API_URL = 'https://api.smartthings.com/v1';
const APP_ID = import.meta.env.VITE_SMARTTHINGS_APP_ID ?? '1cbc6fb2-e026-44c8-9523-35ae9f4d2f01';
const CLIENT_ID = import.meta.env.VITE_SMARTTHINGS_CLIENT_ID ?? 'b99ba7cd-1dda-48b2-957e-fc7d763fd4fe';
// NOTE: a client secret in a browser bundle is not secret. Prefer moving the token exchange/refresh to a backend.
const CLIENT_SECRET = import.meta.env.VITE_SMARTTHINGS_CLIENT_SECRET;
const TOKEN_STORAGE_KEY = 'smartthings_tokens';

// Use HTTPS for both development and production
const REDIRECT_URIS = {
      development: 'https://localhost:3000/oauth/callback',
      production: 'https://mie.mieros.cloud/oauth/callback'
};

export const smartThingsService = {
      // Get devices
      async getDevices(accessToken) {
            try {
                  const response = await fetch(`${SMARTTHINGS_API_URL}/devices`, {
                        method: 'GET',
                        headers: {
                              'Authorization': `Bearer ${accessToken}`,
                              'Accept': 'application/json',
                        }
                  });
                  if (!response.ok) throw new Error('Failed to fetch devices');
                  return await response.json();
            } catch (error) {
                  console.error('SmartThings API Error:', error);
                  throw error;
            }
      },

      // Control device
      async controlDevice(accessToken, deviceId, command) {
            try {
                  const response = await fetch(`${SMARTTHINGS_API_URL}/devices/${deviceId}/commands`, {
                        method: 'POST',
                        headers: {
                              'Authorization': `Bearer ${accessToken}`,
                              'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(command)
                  });
                  if (!response.ok) throw new Error('Failed to control device');
                  return await response.json();
            } catch (error) {
                  console.error('SmartThings API Error:', error);
                  throw error;
            }
      },

      // Get scenes
      async getScenes(accessToken) {
            try {
                  const response = await fetch(`${SMARTTHINGS_API_URL}/scenes`, {
                        headers: {
                              'Authorization': `Bearer ${accessToken}`,
                              'Accept': 'application/json',
                        }
                  });
                  if (!response.ok) throw new Error('Failed to fetch scenes');
                  return await response.json();
            } catch (error) {
                  console.error('SmartThings API Error:', error);
                  throw error;
            }
      },

      // Execute scene
      async executeScene(accessToken, sceneId) {
            try {
                  const response = await fetch(`${SMARTTHINGS_API_URL}/scenes/${sceneId}/execute`, {
                        method: 'POST',
                        headers: {
                              'Authorization': `Bearer ${accessToken}`,
                              'Accept': 'application/json',
                        }
                  });
                  if (!response.ok) throw new Error('Failed to execute scene');
                  return await response.json();
            } catch (error) {
                  console.error('SmartThings API Error:', error);
                  throw error;
            }
      },

      // Get OAuth URL
      getOAuthUrl() {
            const redirectUri = this.getRedirectUri();
            const scope = [
                  'r:devices:*',
                  'x:devices:*',
                  'w:installedapps',
                  'r:installedapps'
            ].join(' ');

            return `https://api.smartthings.com/oauth/authorize?` +
                  `client_id=${CLIENT_ID}&` +
                  `response_type=code&` +
                  `scope=${encodeURIComponent(scope)}&` +
                  `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                  `app_id=${APP_ID}`;
      },

      // Update token exchange to include app_id
      async exchangeCodeForToken(code) {
            try {
                  const redirectUri = this.getRedirectUri();
                  console.log('Exchanging code for token');

                  const response = await fetch(`${SMARTTHINGS_API_URL}/oauth/token`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                              'Accept': 'application/json'
                        },
                        body: JSON.stringify({
                              grant_type: 'authorization_code',
                              code: code,
                              client_id: CLIENT_ID,
                              client_secret: CLIENT_SECRET,
                              redirect_uri: redirectUri
                        })
                  });

                  if (!response.ok) {
                        const errorText = await response.text();
                        console.error('Error response:', {
                              status: response.status,
                              statusText: response.statusText,
                              body: errorText
                        });
                        throw new Error(`Token exchange failed: ${response.status} - ${errorText}`);
                  }
                  
                  const tokens = await response.json();
                  console.log('Token exchange successful:', {
                        ...tokens,
                        access_token: '***hidden***'
                  });

                  this.storeTokens(tokens);
                  return tokens;
            } catch (error) {
                  console.error('Token exchange error details:', error);
                  throw error;
            }
      },

      // Token Storage and Management
      storeTokens(tokens) {
            const tokenData = {
                  access_token: tokens.access_token,
                  refresh_token: tokens.refresh_token,
                  expires_at: Date.now() + (tokens.expires_in * 1000)
            };
            localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
      },

      async getValidToken() {
            const tokenData = JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY));

            if (!tokenData) {
                  console.warn('No token data found');
                  return null;
            }


            // Check if token needs refresh (5 minutes buffer)
            if (Date.now() > tokenData.expires_at - 300000) {
                  console.log('Token expired, refreshing...');
                  return await this.refreshToken(tokenData.refresh_token);
            }

            return tokenData.access_token;
      },

      async refreshToken(refreshToken) {
            try {
                  const response = await fetch(`${SMARTTHINGS_API_URL}/oauth/token`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                              grant_type: 'refresh_token',
                              client_id: CLIENT_ID,
                              client_secret: CLIENT_SECRET,
                              refresh_token: refreshToken
                        })
                  });

                  if (!response.ok) throw new Error('Token refresh failed');

                  const tokens = await response.json();
                  this.storeTokens(tokens);
                  console.log('Token refreshed');
                  return tokens.access_token;
            } catch (error) {
                  console.error('Token refresh error:', error);
                  localStorage.removeItem(TOKEN_STORAGE_KEY); // Clear invalid tokens
                  throw error;
            }
      },

      getRedirectUri() {
            return window.location.hostname === 'localhost' 
                  ? REDIRECT_URIS.development 
                  : REDIRECT_URIS.production;
      },

      async initiateOAuth(returnPath = '/homeassistant/devices') {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            
            if (!currentUser) {
                  console.warn('User not authenticated');
                  sessionStorage.setItem('smartthings_return_path', returnPath);
                  return false;
            }

            sessionStorage.setItem('smartthings_return_path', returnPath);
            
            // Get and redirect to OAuth URL
            const authUrl = this.getOAuthUrl();
            window.location.href = authUrl;
            return true;
      }
}; 